import { HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AtlasLanguageService } from '@galaxy/atlas/core';
import { map } from 'rxjs/operators';
import {
  BulkDeleteCrmObjectsRequestInterface,
  CreateCrmObjectRequestInterface,
  CreateCrmObjectResponseInterface,
  CRMApiService,
  DeleteCrmObjectRequestInterface,
  GetMultiCrmObjectRequestInterface,
  GetMultiCrmObjectResponseInterface,
  ListCrmObjectsRequestInterface,
  ListCrmObjectsResponseInterface,
  ListFieldOptionsRequestInterface,
  ListFiltersRequestInterface,
  ListFiltersResponseInterface,
  UpdateCrmObjectRequestInterface,
  ValidateCrmObjectRequestInterface,
  ValidateCrmObjectResponseInterface,
} from '@vendasta/crm';
import { Observable } from 'rxjs';
import { AbstractCrmObjectService } from './_abstract.service';
import { listContacts$, getMultiContact$ } from '@vendasta/protos/crm/v1/crmservice';
import {
  toLegacyGetMultiResponse,
  toLegacyListResponse,
  toProtoGetMultiRequest,
  toProtoListRequest,
} from '@galaxy/crm/services/adapter-service';

@Injectable({ providedIn: 'root' })
export class ContactService implements AbstractCrmObjectService {
  private readonly languageService = inject(AtlasLanguageService);
  protected locale = toSignal(this.languageService.language$);

  constructor(private readonly service: CRMApiService) {}

  createCrmObject(request: CreateCrmObjectRequestInterface): Observable<CreateCrmObjectResponseInterface> {
    return this.service.createContact(request);
  }

  deleteCrmObject(request: DeleteCrmObjectRequestInterface): Observable<HttpResponse<null>> {
    return this.service.deleteContact(request);
  }

  validateCrmObject(request: ValidateCrmObjectRequestInterface): Observable<ValidateCrmObjectResponseInterface> {
    return this.service.validateContact(request);
  }

  updateCrmObject(request: UpdateCrmObjectRequestInterface): Observable<HttpResponse<null>> {
    return this.service.updateContact(request);
  }

  listObjects(request: ListCrmObjectsRequestInterface): Observable<ListCrmObjectsResponseInterface> {
    return listContacts$(toProtoListRequest(request)).pipe(map(toLegacyListResponse));
  }

  listObjectsFilters(request: ListFiltersRequestInterface): Observable<ListFiltersResponseInterface> {
    return this.service.listContactsFilters({ ...request, locale: this.locale() });
  }

  getMultiObject(request: GetMultiCrmObjectRequestInterface): Observable<GetMultiCrmObjectResponseInterface> {
    return getMultiContact$(toProtoGetMultiRequest(request)).pipe(map(toLegacyGetMultiResponse));
  }

  listFieldOptions(request: ListFieldOptionsRequestInterface): Observable<string[]> {
    return this.service.listContactFieldOptions(request).pipe(map((response) => response?.options || []));
  }

  bulkDelete(request: BulkDeleteCrmObjectsRequestInterface): Observable<HttpResponse<null>> {
    return this.service.bulkDeleteContacts(request);
  }
}
